import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  FiPhone,
  FiMail,
  FiMapPin,
  FiUploadCloud,
  FiCheckCircle,
  FiFileText
} from "react-icons/fi";

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionWrapper = styled.section`
  padding: 6rem 1.5rem;
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors.white},
    ${({ theme }) => theme.colors.bgAccent}
  );
`;

const Container = styled.div`
  max-width: 1280px;
  margin: auto;
`;

const HeaderContainer = styled.div`
  max-width: 750px;
  margin: 0 auto 4rem;
  text-align: center;
`;

const SectionBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.45rem 1.2rem;
  border-radius: 50px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 1rem;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  line-height: 1.15;
  letter-spacing: -1px;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 1rem;
`;

const SectionSubtitle = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const MainGrid = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 3rem;
  align-items: start;

  @media (max-width: ${({ theme }) => theme.breakpoints.desktop}) {
    grid-template-columns: 1fr;
  }
`;

const FormCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  padding: 2.5rem;
  border-radius: 24px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: 0 15px 40px rgba(15, 23, 42, 0.08);
  animation: ${fadeUp} 0.7s ease;
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 800;
  margin-bottom: 1.8rem;
  color: ${({ theme }) => theme.colors.textDark};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;

  label {
    font-size: 0.85rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.textDark};
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.95rem 1rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgLight};
  font-size: 0.95rem;
  outline: none;
  transition: 0.3s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.white};
    box-shadow: 0 0 0 4px rgba(15, 139, 66, 0.12);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.95rem 1rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgLight};
  font-size: 0.95rem;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.95rem 1rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.bgLight};
  font-size: 0.95rem;
  outline: none;
  resize: vertical;
  font-family: inherit;
  transition: 0.3s;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.white};
    box-shadow: 0 0 0 4px rgba(15, 139, 66, 0.12);
  }
`;

const FileUploadLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  border: 2px dashed ${({ theme }) => theme.colors.border};
  border-radius: 16px;
  background: ${({ theme }) => theme.colors.bgLight};
  cursor: pointer;
  text-align: center;
  transition: 0.3s;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primaryLight};
  }

  .upload-text {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.textMuted};
    margin-top: 0.5rem;
  }

  .file-name {
    margin-top: 0.5rem;
    font-size: 0.85rem;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const SubmitButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem;
  border-radius: 12px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 1rem;
  font-weight: 800;
  box-shadow: ${({ theme }) => theme.shadows.md};
  margin-top: 0.5rem;
  border: none;
  cursor: pointer;
  transition: 0.3s;

  &:hover {
    background: ${({ theme }) => theme.colors.primaryHover};
    transform: translateY(-3px);
  }
`;

const ContactColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ContactCard = styled.a`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.3rem;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.sm};
  transition: 0.3s;
  text-decoration: none;

  &:hover {
    transform: translateY(-5px);
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const IconWrapper = styled.div`
  width: 52px;
  height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  background: ${({ theme }) => theme.colors.primaryLight};
  color: ${({ theme }) => theme.colors.primary};
  font-size: 1.4rem;
  flex-shrink: 0;
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;

  .label {
    font-size: 0.75rem;
    font-weight: 800;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.primary};
  }

  .value {
    font-size: 1rem;
    font-weight: 800;
    color: ${({ theme }) => theme.colors.textDark};
  }

  .sub {
    font-size: 0.85rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const MapContainer = styled.div`
  height: 300px;
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.md};

  iframe {
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

export default function Quote() {
  const [file, setFile] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    projectType: "Architectural Plastering",
    message: "",
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const messageText = `*New Quotation Request*

👤 Name: ${formData.name}
📧 Email: ${formData.email}
📱 Phone: ${formData.phone}
🏗️ Project Type: ${formData.projectType}

📝 Project Details:
${formData.message || "None provided"}

${file ? `📎 Attachment: ${file.name}` : "📎 Attachment: Not Provided"}`;

    const whatsappNumber = "971554688022";

    window.open(
      `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(messageText)}`,
      "_blank"
    );

    setSubmitted(true);
  };

  return (
    <SectionWrapper id="contact">
      <Container>
        <HeaderContainer>
          <SectionBadge>
            <FiFileText />
            Get In Touch
          </SectionBadge>

          <SectionTitle>Request Your Project Quotation</SectionTitle>

          <SectionSubtitle>
            Share your requirements, drawings, or BOQ. Our estimation team will
            contact you within 24 hours.
          </SectionSubtitle>
        </HeaderContainer>

        <MainGrid>
          <FormCard>
            <FormTitle>Project Quotation Form</FormTitle>

            {submitted ? (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <FiCheckCircle size={45} color="#0F8B42" />
                <h3>Request Submitted Successfully</h3>
                <p style={{ marginTop: "0.5rem" }}>
                  Our team will review your requirements and contact you shortly.
                </p>
              </div>
            ) : (
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <label htmlFor="name">Full Name *</label>
                  <Input
                    id="name"
                    name="name"
                    placeholder="Enter your name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="email">Email Address *</label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="name@email.com"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="phone">Phone / WhatsApp *</label>
                  <Input
                    id="phone"
                    name="phone"
                    placeholder="+971 XX XXX XXXX"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <label htmlFor="projectType">Project Type</label>
                  <Select
                    id="projectType"
                    name="projectType"
                    value={formData.projectType}
                    onChange={handleInputChange}
                  >
                    <option value="Architectural Plastering">
                      Architectural Plastering
                    </option>
                    <option value="Floor & Wall Tiling">
                      Floor & Wall Tiling
                    </option>
                    <option value="Full Fit-out">Full Fit-out</option>
                    <option value="Waterproofing">Waterproofing</option>
                    <option value="Others">Others</option>
                  </Select>
                </FormGroup>

                <FormGroup>
                  <label htmlFor="message">Project Details</label>
                  <Textarea
                    id="message"
                    name="message"
                    rows="5"
                    placeholder="Describe your project..."
                    value={formData.message}
                    onChange={handleInputChange}
                  />
                </FormGroup>

                <FormGroup>
                  <label>Upload Drawings / BOQ</label>
                  <FileUploadLabel htmlFor="upload">
                    <FiUploadCloud size={35} />
                    Choose PDF, CAD or Image File
                    <span className="upload-text">
                      Upload project documents for faster quotation
                    </span>
                    {file && <span className="file-name">{file.name}</span>}
                  </FileUploadLabel>

                  <HiddenFileInput
                    id="upload"
                    type="file"
                    onChange={(e) => setFile(e.target.files[0])}
                  />
                </FormGroup>

                <SubmitButton type="submit">
                  <FiCheckCircle />
                  Submit Request
                </SubmitButton>
              </Form>
            )}
          </FormCard>

          <ContactColumn>
            <ContactCard href="tel:+971554688022">
              <IconWrapper>
                <FiPhone />
              </IconWrapper>
              <CardContent>
                <span className="label">Call / WhatsApp</span>
                <span className="value">+971 55 468 8022</span>
                <span className="sub">Direct Project Inquiry</span>
              </CardContent>
            </ContactCard>

            <ContactCard href="mailto:jisralsahra@gmail.com">
              <IconWrapper>
                <FiMail />
              </IconWrapper>
              <CardContent>
                <span className="label">Email</span>
                <span className="value">jisralsahra@gmail.com</span>
                <span className="sub">Official Business Email</span>
              </CardContent>
            </ContactCard>

            <ContactCard
              href="https://maps.google.com/?q=Al+Barajeel+Building+Al+Nasiriya+Sharjah"
              target="_blank"
              rel="noopener noreferrer"
            >
              <IconWrapper>
                <FiMapPin />
              </IconWrapper>
              <CardContent>
                <span className="label">Office Location</span>
                <span className="value">Al Barajeel Building</span>
                <span className="sub">Al Nasiriya, Sharjah UAE</span>
              </CardContent>
            </ContactCard>

            <MapContainer>
              <iframe
                title="Office Location - Al Barajeel Building, Al Nasiriya, Sharjah"
                src="https://maps.google.com/maps?q=Al%20Barajeel%20Building%20Al%20Nasiriya%20Sharjah&t=&z=16&ie=UTF8&iwloc=&output=embed"
                loading="lazy"
                allowFullScreen
              />
            </MapContainer>
          </ContactColumn>
        </MainGrid>
      </Container>
    </SectionWrapper>
  );
}